import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { User, LoginCredentials, RegisterData, AuthResponse } from '@/lib/types';
import api, { endpoints } from '@/services/api';
import { getApiErrorMessage } from '@/lib/utils';
import {
  clearPersistedAuthUser,
  persistAuthUser,
  readPersistedAuthUser,
} from '@/services/authPersistence';

export interface AuthRequestFailure {
  message: string;
  status?: number;
}

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const response = (error as { response?: unknown }).response;
  if (typeof response !== 'object' || response === null) return undefined;
  const status = (response as { status?: unknown }).status;
  return typeof status === 'number' ? status : undefined;
};

const toAuthRequestFailure = (error: unknown, fallback: string): AuthRequestFailure => {
  const status = getErrorStatus(error);
  return {
    message: getApiErrorMessage(error, fallback),
    ...(status !== undefined ? { status } : {}),
  };
};

const isExplicitAuthFailure = (failure: AuthRequestFailure | undefined): boolean => (
  failure?.status === 401
);

const persistAuthResponse = (payload: AuthResponse): AuthResponse => {
  persistAuthUser(payload.user);
  return payload;
};

interface AuthState {
  user: User | null;
  token: string | null; // Keep for backward compatibility, but primarily use cookies
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean; // Track if we've checked authentication status
  error: string | null;
}

const persistedAuthUser = readPersistedAuthUser();

const initialState: AuthState = {
  user: persistedAuthUser,
  token: null,
  isAuthenticated: Boolean(persistedAuthUser),
  isLoading: false,
  isInitialized: true,
  error: null,
};

// Async thunks
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (_, { rejectWithValue }) => {
    try {
      // [T24P0] Response shape fix: backend /auth/me returns { success, user } not { success, data }.
      // The mismatch caused UserNav to always show 'User' / 'No email' / 'US' avatar.
      const response = await api.get<{ success: boolean; user?: User; data?: User }>(endpoints.auth.me);
      const user = response.data.user || response.data.data; // support both shapes
      if (!user) {
        return rejectWithValue({ message: 'No user data in response' });
      }
      persistAuthUser(user);
      return user;
    } catch (error: unknown) {
      return rejectWithValue(toAuthRequestFailure(error, 'Failed to fetch user'));
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: LoginCredentials, { rejectWithValue }) => {
    try {
      const response = await api.post<AuthResponse>(endpoints.auth.login, credentials);
      // Cookies are set automatically by the backend via Set-Cookie headers
      return persistAuthResponse(response.data);
    } catch (error: unknown) {
      // Network error (CORS blocked, timeout, wrong URL)
      if (typeof error === 'object' && error !== null && 'code' in error &&
          (error as { code: string }).code === 'ERR_NETWORK') {
        return rejectWithValue('Cannot reach the server. Check your connection.');
      }
      return rejectWithValue(getApiErrorMessage(error, 'Login failed'));
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: RegisterData, { rejectWithValue }) => {
    try {
      const response = await api.post<AuthResponse>(endpoints.auth.register, userData);
      // Cookies are set automatically by the backend via Set-Cookie headers
      return persistAuthResponse(response.data);
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Registration failed'));
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await api.post(endpoints.auth.logout);
      // Cookies are cleared automatically by the backend
      return;
    } catch (error: unknown) {
      // Even if the API call fails, clear local state
      console.error('Logout API error:', error);
      return rejectWithValue(getApiErrorMessage(error, 'Logout failed'));
    }
  }
);

export const logoutAll = createAsyncThunk(
  'auth/logoutAll',
  async (_, { rejectWithValue }) => {
    try {
      await api.post(endpoints.auth.logoutAll);
      return;
    } catch (error: unknown) {
      console.error('Logout all devices API error:', error);
      return rejectWithValue(getApiErrorMessage(error, 'Logout all failed'));
    }
  }
);

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.post<{ success: boolean; token?: string }>(endpoints.auth.refresh);
      // New access token cookie is set automatically by the backend
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(toAuthRequestFailure(error, 'Token refresh failed'));
    }
  }
);

export const changePassword = createAsyncThunk(
  'auth/changePassword',
  async (data: { currentPassword: string; newPassword: string }, { rejectWithValue }) => {
    try {
      const response = await api.put(endpoints.auth.changePassword, data);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to change password'));
    }
  }
);

export const forgotPassword = createAsyncThunk(
  'auth/forgotPassword',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.auth.forgotPassword, { email });
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send reset email'));
    }
  }
);

export const resetPasswordAction = createAsyncThunk(
  'auth/resetPassword',
  async (data: { token: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.auth.resetPassword, data);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to reset password'));
    }
  }
);

// OTP Authentication Thunks
export const requestLoginOtp = createAsyncThunk(
  'auth/requestLoginOtp',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.auth.otpLoginRequest, { email });
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send OTP'));
    }
  }
);

export const verifyLoginOtp = createAsyncThunk(
  'auth/verifyLoginOtp',
  async ({ email, code }: { email: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await api.post<AuthResponse>(endpoints.auth.otpLoginVerify, { email, code });
      return persistAuthResponse(response.data);
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'OTP verification failed'));
    }
  }
);

export const requestRegisterOtp = createAsyncThunk(
  'auth/requestRegisterOtp',
  async (email: string, { rejectWithValue }) => {
    try {
      const response = await api.post(endpoints.auth.otpRegisterRequest, { email });
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to send OTP'));
    }
  }
);

export const verifyRegisterOtp = createAsyncThunk(
  'auth/verifyRegisterOtp',
  async ({ email, code, firstName, lastName }: { email: string; code: string; firstName: string; lastName: string }, { rejectWithValue }) => {
    try {
      const response = await api.post<AuthResponse>(endpoints.auth.otpRegisterVerify, { email, code, firstName, lastName });
      return persistAuthResponse(response.data);
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'OTP registration failed'));
    }
  }
);

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (data: { firstName?: string; lastName?: string; preferences?: User['preferences'] }, { rejectWithValue }) => {
    try {
      const response = await api.put<{ success: boolean; user: User }>(endpoints.auth.profile, data);
      persistAuthUser(response.data.user);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to update profile'));
    }
  }
);

export const deleteAccountAction = createAsyncThunk(
  'auth/deleteAccount',
  async (data: { confirmDelete: string; password?: string }, { rejectWithValue }) => {
    try {
      const response = await api.delete(endpoints.auth.deleteAccount, { data });
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to delete account'));
    }
  }
);

export const exportUserData = createAsyncThunk(
  'auth/exportUserData',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get(endpoints.auth.exportData);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to export data'));
    }
  }
);

export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (payload: { credential?: string; accessToken?: string } | string, { rejectWithValue }) => {
    try {
      // Accept legacy string form (treated as accessToken) or explicit object
      const body = typeof payload === 'string' ? { accessToken: payload } : payload;
      const response = await api.post<AuthResponse>(endpoints.auth.googleAuth, body);
      return persistAuthResponse(response.data);
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Google sign-in failed'));
    }
  }
);

export const unlinkGoogleAction = createAsyncThunk(
  'auth/unlinkGoogle',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.delete<{ success: boolean; message: string }>(endpoints.auth.unlinkGoogle);
      return response.data;
    } catch (error: unknown) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to unlink Google'));
    }
  }
);

// Slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (state, action: PayloadAction<{ user: User; token?: string }>) => {
      persistAuthUser(action.payload.user);
      state.user = action.payload.user;
      state.token = action.payload.token || null;
      state.isAuthenticated = true;
      state.isInitialized = true;
    },
    clearAuth: (state) => {
      clearPersistedAuthUser();
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      state.isInitialized = true;
      state.error = null;
      // Cookies are cleared by the backend on logout
    },
    clearError: (state) => {
      state.error = null;
    },
    setInitialized: (state) => {
      state.isInitialized = true;
    },
  },
  extraReducers: (builder) => {
    // Fetch Current User (on app initialization)
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        persistAuthUser(action.payload);
        state.isLoading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        const failure = action.payload as AuthRequestFailure | undefined;
        if (isExplicitAuthFailure(failure)) {
          clearPersistedAuthUser();
          state.user = null;
          state.isAuthenticated = false;
        }
        state.isLoading = false;
        state.isInitialized = true;
        state.error = failure?.message ?? null;
      });

    // Login
    builder
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        persistAuthUser(action.payload.user);
        state.isLoading = false;
        state.user = action.payload.user;
        // Support both old 'token' and new 'accessToken' fields
        state.token = action.payload.accessToken || action.payload.token || null;
        state.isAuthenticated = true;
        state.isInitialized = true;
        // Token is stored in httpOnly cookie by backend
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      });

    // Register
    builder
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        persistAuthUser(action.payload.user);
        state.isLoading = false;
        state.user = action.payload.user;
        // Support both old 'token' and new 'accessToken' fields
        state.token = action.payload.accessToken || action.payload.token || null;
        state.isAuthenticated = true;
        state.isInitialized = true;
        // Token is stored in httpOnly cookie by backend
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.error = action.payload as string;
      });

    // Logout
    builder
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        clearPersistedAuthUser();
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
        // Cookie is cleared by backend
      })
      .addCase(logout.rejected, (state) => {
        clearPersistedAuthUser();
        // Even if logout fails on backend, clear local state
        state.isLoading = false;
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Logout All Devices
    builder
      .addCase(logoutAll.fulfilled, (state) => {
        clearPersistedAuthUser();
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      })
      .addCase(logoutAll.rejected, (state) => {
        clearPersistedAuthUser();
        // Even if logout fails on backend, clear local state
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Refresh Token
    builder
      .addCase(refreshToken.fulfilled, (state, action) => {
        // New access token is set in cookie by backend
        state.token = action.payload.token || null;
      })
      .addCase(refreshToken.rejected, (state, action) => {
        const failure = action.payload as AuthRequestFailure | undefined;
        if (isExplicitAuthFailure(failure)) {
          clearPersistedAuthUser();
          state.user = null;
          state.token = null;
          state.isAuthenticated = false;
        }
        state.error = failure?.message ?? null;
      });

    // Update Profile
    builder
      .addCase(updateProfile.fulfilled, (state, action) => {
        if (action.payload.user) {
          persistAuthUser(action.payload.user);
          state.user = action.payload.user;
        }
      });

    // Delete Account
    builder
      .addCase(deleteAccountAction.fulfilled, (state) => {
        clearPersistedAuthUser();
        state.user = null;
        state.token = null;
        state.isAuthenticated = false;
      });

    // Google Login / Register
    builder
      .addCase(googleLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        persistAuthUser(action.payload.user);
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken || action.payload.token || null;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Unlink Google
    builder
      .addCase(unlinkGoogleAction.fulfilled, (state) => {
        if (state.user) {
          state.user = {
            ...state.user,
            authProviders: state.user.authProviders?.filter((p) => p !== 'google') ?? [],
          };
        }
      });

    // OTP Login Request
    builder
      .addCase(requestLoginOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestLoginOtp.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(requestLoginOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // OTP Login Verify
    builder
      .addCase(verifyLoginOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyLoginOtp.fulfilled, (state, action) => {
        persistAuthUser(action.payload.user);
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken || action.payload.token || null;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(verifyLoginOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // OTP Register Request
    builder
      .addCase(requestRegisterOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(requestRegisterOtp.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(requestRegisterOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // OTP Register Verify
    builder
      .addCase(verifyRegisterOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyRegisterOtp.fulfilled, (state, action) => {
        persistAuthUser(action.payload.user);
        state.isLoading = false;
        state.user = action.payload.user;
        state.token = action.payload.accessToken || action.payload.token || null;
        state.isAuthenticated = true;
        state.isInitialized = true;
      })
      .addCase(verifyRegisterOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCredentials, clearAuth, clearError } = authSlice.actions;
export default authSlice.reducer;
